import { getUrlTitle, toURL } from "@prismicio/editor-support/URL"
import {
  ErrorBoundary,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
  Popover
} from "@prismicio/editor-ui"
import type { LinkContent } from "@prismicio/types-internal/lib/content"
import type { Link } from "@prismicio/types-internal/lib/customtypes"
import { type PropsWithChildren, Suspense, useState } from "react"

import { useEditorConfig } from "../../EditorConfig"
import { FieldTextInput } from "../FieldTextInput"
import { MediaDialog } from "../ImageField/MediaDialog"
import { getFieldLabel } from "../utils"
import { DocumentsModal } from "./Documents/DocumentsModal"
import { LinkDescriptionCard } from "./LinkDescriptionCard"
import { type FieldSelectTypes, LinkFieldActions } from "./LinkFieldActions"
import { LinkImageCard } from "./LinkImageCard"
import { useDocumentTitle, useLinkField } from "./useLinkField"

interface LinkFieldProps {
  id?: string
  field: Link
  readOnly?: boolean
  content: LinkContent | undefined
  condensedButtons?: boolean
  onContentChange: (content: LinkContent | undefined) => void
}

export function LinkField(props: LinkFieldProps) {
  const {
    content,
    field,
    id,
    onContentChange,
    readOnly = false,
    condensedButtons = false
  } = props

  const [isOverField, setIsOverField] = useState(false)
  const [isOverPopover, setIsOverPopover] = useState(false)
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false)
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false)

  const {
    contentType,
    fieldValue,
    errorBoundaryRef,
    onDocumentSelected,
    onError,
    onMediaSelected,

    ...textInputProps
  } = useLinkField({ content, field, onContentChange })

  const isPopoverOpen = isOverField || isOverPopover

  const isReadOnly = readOnly || !contentType
  const hasDocumentValue = contentType === "DocumentLink" && fieldValue !== ""
  const isMediaLink = contentType === "ImageLink" || contentType === "FileLink"
  const isWebLink = contentType === "ExternalLink"
  const validWebLinkUrl = isWebLink && toURL(fieldValue)

  const sharedInputProps: LinkFieldTextInputProps = {
    id,
    contentType,
    onBlur: () => setIsOverField(false),
    onFocus: () => {
      setIsOverField(true)
      if (contentType === "DocumentLink") setIsDocumentsModalOpen(true)
      if (contentType === "FileLink") setIsMediaPickerOpen(true)
      if (contentType === "ImageLink") setIsMediaPickerOpen(true)
    },
    readOnly: isReadOnly,
    textType: validWebLinkUrl || isMediaLink ? "link" : "normal",
    value: fieldValue,
    condensedButtons,
    ...textInputProps
  }

  const { searchDocuments } = useEditorConfig()

  return (
    <>
      <Popover
        onMouseOverChange={setIsOverPopover}
        open={isPopoverOpen}
        align="start"
        sideOffset={-3}
        openDelay={100}
        closeDelay={200}
        focusOnEnter={false}
        anchor={
          <Field onMouseOverChange={setIsOverField}>
            <FieldIcon name="link" />
            <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
            <FieldControl>
              {hasDocumentValue && searchDocuments ? (
                <ErrorBoundaryAndSuspenseWrapper
                  errorBoundaryRef={errorBoundaryRef}
                  onError={onError}
                  fallback={
                    <LinkFieldTextInput {...sharedInputProps} value="..." />
                  }
                  renderError={() => renderError(sharedInputProps)}
                >
                  <DocumentContent
                    {...sharedInputProps}
                    documentId={fieldValue}
                    openDocumentListModal={() => setIsDocumentsModalOpen(true)}
                  />
                </ErrorBoundaryAndSuspenseWrapper>
              ) : (
                <LinkFieldTextInput {...sharedInputProps} />
              )}
            </FieldControl>
          </Field>
        }
      >
        {validWebLinkUrl && (
          <LinkDescriptionCard
            url={validWebLinkUrl}
            title={getUrlTitle(validWebLinkUrl) ?? ""}
            subTitle={validWebLinkUrl.toString()}
          />
        )}

        {content?.value.__TYPE__ === "ImageLink" && (
          <LinkImageCard
            url={content.value.url}
            title={content.value.name}
            subTitle={`${content.value.width} x ${content.value.height} pixels`}
          />
        )}
      </Popover>

      <MediaDialog
        open={isMediaPickerOpen}
        onOpenChange={setIsMediaPickerOpen}
        onMediaSelected={onMediaSelected}
        assetType="all"
      />

      <DocumentsModal
        open={isDocumentsModalOpen}
        onOpenChange={setIsDocumentsModalOpen}
        onSelectDocument={(documentId) => {
          setIsDocumentsModalOpen(false)
          onDocumentSelected(documentId)
        }}
        fixedCustomTypes={new Set(field.config?.customtypes)}
        fixedTags={new Set(field.config?.tags)}
      />
    </>
  )
}

function renderError(props: LinkFieldTextInputProps) {
  return <LinkFieldTextInput {...props} textType="link" />
}

interface BaseInputProps {
  id?: string
  onValueChange: (newValue: string) => void
  isTypeSelectEnabled: boolean
  selectType?: FieldSelectTypes
  condensedButtons: boolean
  onTypeChange: (newValue: FieldSelectTypes) => void
}

interface LinkFieldTextInputProps extends BaseInputProps {
  placeholder?: string
  readOnly: boolean
  value: string
  textType: "link" | "normal"
  contentType?: string
  isTargetNewTab: boolean
  onFocus: () => void
  onBlur: () => void
  updateWebLinkTarget: (newTab: boolean) => void
}

function LinkFieldTextInput(props: LinkFieldTextInputProps) {
  const {
    id,
    onValueChange,
    placeholder,
    readOnly,
    value,
    textType,
    onFocus,
    onBlur,
    contentType,
    isTypeSelectEnabled,
    selectType,
    onTypeChange,
    isTargetNewTab,
    condensedButtons,
    updateWebLinkTarget
  } = props

  const isLink =
    contentType === "DocumentLink" ||
    contentType === "ImageLink" ||
    contentType === "FileLink"

  return (
    <FieldTextInput
      id={id}
      onValueChange={onValueChange}
      placeholder={placeholder}
      readOnly={readOnly || isLink}
      value={value}
      variant={textType}
      onFocus={onFocus}
      onBlur={onBlur}
      cursor={isLink ? "pointer" : "text"}
      endAdornment={
        <LinkFieldActions
          hasLink={Boolean(value)}
          onUnlink={() => onValueChange("")}
          selectEnabled={isTypeSelectEnabled}
          selectedType={selectType}
          onValueChange={onTypeChange}
          openInNewTab={isTargetNewTab}
          condensedButtons={condensedButtons}
          setOpenInNewTab={updateWebLinkTarget}
        />
      }
    />
  )
}

interface DocumentContentProps extends BaseInputProps {
  documentId: string
  openDocumentListModal: () => void
}

function DocumentContent(props: DocumentContentProps) {
  const {
    id,
    documentId,
    openDocumentListModal,
    onValueChange,
    isTypeSelectEnabled,
    selectType,
    condensedButtons,
    onTypeChange
  } = props

  const documentTitle = useDocumentTitle(documentId)

  return (
    <FieldTextInput
      id={id}
      readOnly
      value={documentTitle}
      variant="link"
      onFocus={openDocumentListModal}
      cursor="pointer"
      endAdornment={
        <LinkFieldActions
          hasLink
          onUnlink={() => onValueChange("")}
          selectEnabled={isTypeSelectEnabled}
          selectedType={selectType}
          condensedButtons={condensedButtons}
          onValueChange={onTypeChange}
        />
      }
    />
  )
}

interface ErrorBoundartAndSuspsenseWrapperProps extends PropsWithChildren {
  errorBoundaryRef: React.RefObject<ErrorBoundary>
  onError: VoidFunction
  renderError: () => React.ReactNode
  fallback: React.ReactNode
}

function ErrorBoundaryAndSuspenseWrapper(
  props: ErrorBoundartAndSuspsenseWrapperProps
) {
  const { errorBoundaryRef, onError, children, fallback, renderError } = props

  return (
    <ErrorBoundary
      ref={errorBoundaryRef}
      onError={onError}
      renderError={renderError}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}
